import { IOnboardingRepository } from '../../domain/repositories/IOnboardingRepository';
import { OnboardingProgressDTO } from '../dto/OnboardingProgressDTO';
import { OnboardingMapper } from '../mappers/OnboardingMapper';

/**
 * CompleteOnboardingStepUseCase
 * Marks current step as complete and advances to next step
 *
 * Business Logic:
 * - Records step completion for analytics (non-blocking)
 * - Updates step index to next step
 * - If reaching end, status becomes 'completed'
 * - Returns updated progress DTO
 */
export interface CompleteStepParams {
  userId: string;
  role: string;
  stepId: string;
  nextStepIndex: number;
}

export class CompleteOnboardingStepUseCase {
  constructor(private readonly repository: IOnboardingRepository) {}

  /**
   * Execute the use case
   *
   * @param params - Step completion parameters
   * @returns Promise<OnboardingProgressDTO> - Updated progress
   */
  async execute(params: CompleteStepParams): Promise<OnboardingProgressDTO> {
    const { userId, role, stepId, nextStepIndex } = params;

    // Record step completion for analytics (non-blocking, doesn't throw)
    await this.repository.completeStep(userId, role, stepId).catch(error => {
      console.error('Failed to record step completion (non-critical):', error);
    });

    // Update progress to next step
    const progress = await this.repository.updateStep(userId, role, nextStepIndex);

    return OnboardingMapper.progressToDTO(progress);
  }
}
